/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import config from '../../../../share/config';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  FAILED: 'failed'
};

export default {
  name: 'requirement091',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      isErrorCheck: false, // 終了処理のレスポンス判定フラグ
      isSession: false, // Session遷移判定フラグ
      displayInfoFotaStatus: '', // ステータス読み出し結果
      displayInfoFotaDetail: '', // 詳細読み出し結果
      modal_requirement_091: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化処理を呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      this.modal_requirement_091 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // ステータスデータ読み出し処理
      this.readStatusPostGet();
    },

    //----- htmlイベント処理 -----------------------------------------------//

    /** 画面を閉じるボタンクリックイベント */
    async clickClose() {
      this.isLoading = true;
      // 画面を閉じる処理（セッション遷移済なら終了処理を呼び出す）
      if (!this.isSession || await this.exitPostGet()) {
        // Dispose処理
        await this.disposePostGet();
        this.$emit('close-wizard');
        this.modal_requirement_091 = false;
      }
      this.isLoading = false;
    },

    //----- PHASE操作処理 -----------------------------------------------//

    /** 読出し結果表示処理 */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
      // 本遷移処理以降は終了処理でエラー判定を行う
      this.isErrorCheck = true;
    },

    /** 共通エラー表示処理 */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行処理 -----------------------------------------------//

    /**
     * ステータスデータ読み出し処理
     */
    async readStatusPostGet() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read_status'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // Session遷移判定フラグを取得
            this.isSession = response.data.user_data.is_session;
            // ステータス読み出し結果の翻訳結果文言を取得
            const result = await individualWorkSupportUtil.getReadOutData(
              this, this.systemId, response.data.user_data);
            // 項目表示名と該当自由表示項目の翻訳結果文言を設定
            this.displayInfoFotaStatus = result.replace(':', '\n  ');
            // FOTA実施の有無で分岐
            const fotaStatus = response.data.user_data.readout_data[0].data_record;
            if (fotaStatus !== '01') {
              // FOTA実施済の場合、詳細データ読み出し処理を呼び出す
              await this.readDetailPostGet(fotaStatus);
            } else {
              // FOTA未実施の場合、読出し結果表示画面に遷移
              this.showSuccess();
            }
          } else {
            // [成功以外]
            // 「FOTA 実施結果(DID0x10E1)応答値」に該当しない場合も含む
            // 共通エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 詳細データ読み出し処理
     * @param {object} fotaStatus FOTA実施結果応答値
     */
    async readDetailPostGet(fotaStatus) {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read_detail',
            fota_status: fotaStatus
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 詳細読み出し結果の翻訳結果文言を取得
            const resultData = await individualWorkSupportUtil.getReadOutData(
              this, this.systemId, response.data.user_data);
            const resultAry = resultData.split('\n');
            resultAry.forEach(result => {
              if (result) {
                // 項目表示名と該当自由表示項目の翻訳結果文言を設定
                this.displayInfoFotaDetail += '\n';
                this.displayInfoFotaDetail += result.replace(':', '\n  ');
                this.displayInfoFotaDetail += '\n';
              }
            });
            // 読出し結果表示画面に遷移
            this.showSuccess();
          } else {
            // [成功以外]
            // 共通エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 終了処理
     * @return 終了判定フラグ
     */
    async exitPostGet() {
      // バックエンドAPI POST,GET
      const token = session.getToken();
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'exit'
        },
        // エラー発生のコールバックを実装
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
        (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (this.isErrorCheck) {
        // 終了処理のエラー判定を行う場合
        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            return true;
          } else {
            // [成功以外]
            // 共通エラー画面に遷移
            this.showUnexpectedError();
            return false;
          }
        } else {
          // レスポンスが取得できない場合、コールバックでエラー画面に遷移
          return false;
        }
      } else {
        return true;
      }
    },

    /**
     * Dispose処理
     */
    async disposePostGet() {
      // バックエンドAPI POST,GET
      const token = session.getToken();
      await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'dispose'
        },
        // エラー発生のコールバックを実装
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
        (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 終了処理を呼ぶためにSession遷移判定フラグを更新
      this.isSession = true;
      // 共通エラー画面では終了処理のレスポンス判定は行わない
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.showFailed();
    },

  }
};